import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import {
  Box,
  VStack,
  HStack,
  Flex,
  Text,
  Avatar,
  Button,
  Input,
  InputGroup,
  IconButton,
  Tag,
  TagLabel,
  Wrap,
  WrapItem,
  Icon,
  Collapse,
  useDisclosure,
  useToast,
  Circle,
  Spinner,
  Badge,
} from '@chakra-ui/react'
import {
  FiUser,
  FiMapPin,
  FiDollarSign,
  FiCalendar,
  FiBriefcase,
  FiChevronDown,
  FiChevronUp,
  FiSend,
  FiSquare,
  FiCpu,
  FiLink2,
  FiAlertCircle,
  FiBookmark,
} from 'react-icons/fi'
import { FaRobot, FaCopy } from 'react-icons/fa'
import { HiSparkles } from 'react-icons/hi2'
import ReactMarkdown from 'react-markdown'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { FileItem, FileSummary, KeyEntity, FileLinkRow } from '@/types'
import apiService from '@/services/api'
import { useTranslation } from 'react-i18next'
import { useAppTranslation, useLanguage } from '@/contexts/LanguageContext'
import { MotionBox } from '@/components/Common/MotionBox'
import { toConversationHistory } from '@/components/Chatbot/types'
import { isUsageLimitError, useChatStream } from '@/hooks/useChatStream'
import NotesSection from './NotesSection'
import { useFileNotes } from '@/hooks/useFileNotes'

interface ChatMessage {
  id: string
  /** 'notice' = an error or quota message: shown, never sent back as history */
  type: 'user' | 'assistant' | 'notice'
  content: string
  timestamp: Date
  suggestedQuestions?: string[]
  status?: 'stopped' | 'interrupted'
  excludeFromHistory?: boolean
}

interface PreviewSidebarProps {
  file: FileItem
  tags: string[]
  summaries: FileSummary[]
  fileLinksOutgoing?: FileLinkRow[]
  fileLinksIncoming?: FileLinkRow[]
  onFileLinksChanged?: () => void
}

const entityIcons: Record<string, React.ElementType> = {
  person: FiUser,
  organization: FiBriefcase,
  location: FiMapPin,
  monetary: FiDollarSign,
  date: FiCalendar,
  project: FiCpu,
}

const entityColors: Record<string, string> = {
  person: 'blue',
  organization: 'purple',
  location: 'teal',
  monetary: 'green',
  date: 'orange',
  project: 'cyan',
}

function LinkedDocumentsSection({
  file,
  outgoing,
  incoming,
  onRefresh,
}: {
  file: FileItem
  outgoing: FileLinkRow[]
  incoming: FileLinkRow[]
  onRefresh: () => void
}) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const toast = useToast()
  const { t } = useAppTranslation('files')
  const [unlinkingId, setUnlinkingId] = useState<number | null>(null)

  const goPreview = (id: number) => {
    const qs = searchParams.toString()
    navigate(qs ? `/preview/${id}?${qs}` : `/preview/${id}`)
  }

  const handleUnlink = async (linkId: number) => {
    setUnlinkingId(linkId)
    try {
      await apiService.deleteFileLink(file.id, linkId)
      toast({
        title: t('filePreview.linkDocuments.unlinkSuccess'),
        status: 'success',
        duration: 2500,
        isClosable: true,
      })
      onRefresh()
    } catch {
      toast({
        title: t('filePreview.linkDocuments.unlinkError'),
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
    } finally {
      setUnlinkingId(null)
    }
  }

  if (outgoing.length === 0 && incoming.length === 0) {
    return null
  }

  const renderRow = (row: FileLinkRow, direction: 'outgoing' | 'incoming') => {
    const typeLabel =
      direction === 'outgoing'
        ? t(`filePreview.linkTypes.${row.relation_type}`)
        : t(`filePreview.linkTypes.inverse.${row.relation_type}`)
    return (
      <HStack key={row.id} w="full" justify="space-between" spacing={3} align="center">
        <Box flex={1} minW={0}>
          <Button
            variant="link"
            size="sm"
            fontWeight="500"
            color="brand.600"
            onClick={() => goPreview(row.file.id)}
            noOfLines={2}
            justifyContent="flex-start"
            textAlign="left"
            whiteSpace="normal"
            h="auto"
            py={1}
            px={0}
          >
            {row.file.display_name || row.file.original_name}
          </Button>
          <Badge colorScheme="brand" variant="subtle" fontSize="0.6rem" display="block" w="fit-content">
            {typeLabel}
          </Badge>
        </Box>
        <Button
          size="xs"
          variant="outline"
          colorScheme="red"
          flexShrink={0}
          isLoading={unlinkingId === row.id}
          isDisabled={unlinkingId !== null && unlinkingId !== row.id}
          onClick={() => void handleUnlink(row.id)}
        >
          {t('filePreview.linkDocuments.removeLink')}
        </Button>
      </HStack>
    )
  }

  return (
    <Box pb={4} mb={4} borderBottomWidth="1px" borderColor="gray.200">
      <HStack spacing={2} mb={3} justify="flex-start">
        <Icon as={FiLink2} color="brand.500" />
        <Text fontSize="sm" fontWeight="600" color="gray.800" textAlign="left">
          {t('filePreview.linkDocuments.sectionTitle')}
        </Text>
      </HStack>
      {outgoing.length > 0 && (
        <Box mb={incoming.length > 0 ? 3 : 0}>
          <Text fontSize="xs" color="gray.500" mb={1} textAlign="left">
            {t('filePreview.linkDocuments.outgoing')}
          </Text>
          <VStack align="stretch" spacing={1}>
            {outgoing.map((row) => renderRow(row, 'outgoing'))}
          </VStack>
        </Box>
      )}
      {incoming.length > 0 && (
        <Box>
          <Text fontSize="xs" color="gray.500" mb={1} textAlign="left">
            {t('filePreview.linkDocuments.incoming')}
          </Text>
          <VStack align="stretch" spacing={1}>
            {incoming.map((row) => renderRow(row, 'incoming'))}
          </VStack>
        </Box>
      )}
    </Box>
  )
}

function BotMarkdown({ children }: { children: string }) {
  return (
    <ReactMarkdown
      components={{
        p: ({ children }) => (
          <Text fontSize="sm" mb={2} _last={{ mb: 0 }}>
            {children}
          </Text>
        ),
        h1: ({ children }) => (
          <Text fontSize="md" fontWeight="bold" mb={2}>
            {children}
          </Text>
        ),
        h2: ({ children }) => (
          <Text fontSize="sm" fontWeight="bold" mb={2}>
            {children}
          </Text>
        ),
        h3: ({ children }) => (
          <Text fontSize="sm" fontWeight="bold" mb={1}>
            {children}
          </Text>
        ),
        code: ({ children, className }) => {
          const isInline = !className
          if (isInline) {
            return (
              <Text
                as="code"
                bg="gray.100"
                px={1}
                borderRadius="sm"
                fontSize="xs"
                fontFamily="mono"
              >
                {children}
              </Text>
            )
          }
          return (
            <Box
              as="pre"
              bg="gray.100"
              p={2}
              borderRadius="md"
              fontSize="xs"
              fontFamily="mono"
              overflowX="auto"
              my={2}
            >
              <Text as="code">{children}</Text>
            </Box>
          )
        },
        ul: ({ children }) => (
          <Box as="ul" pl={4} my={1}>
            {children}
          </Box>
        ),
        ol: ({ children }) => (
          <Box as="ol" pl={4} my={1}>
            {children}
          </Box>
        ),
        li: ({ children }) => (
          <Text as="li" fontSize="sm" mb={1}>
            {children}
          </Text>
        ),
        strong: ({ children }) => (
          <Text as="strong" fontWeight="bold">
            {children}
          </Text>
        ),
        em: ({ children }) => (
          <Text as="em" fontStyle="italic">
            {children}
          </Text>
        ),
      }}
    >
      {children}
    </ReactMarkdown>
  )
}

function AISummarySection({ summaries, userLanguage }: { summaries: FileSummary[]; userLanguage: string }) {
  const { t } = useTranslation('files')
  const [isExpanded, setIsExpanded] = useState(false)

  const displaySummary = useMemo(() => {
    const preferred = summaries.find(s => s.language === userLanguage && s.status === 'completed')
    const fallback = summaries.find(s => s.language === 'en' && s.status === 'completed')
    const any = summaries.find(s => s.status === 'completed')
    return preferred || fallback || any
  }, [summaries, userLanguage])

  const isProcessing = summaries.some(s => s.status === 'processing')

  return (
    <Box>
      {isProcessing ? (
        <HStack spacing={2}>
          <Spinner size="sm" color="brand.500" />
          <Text fontSize="sm" color="gray.600">{t('previewSidebar.generatingSummary')}</Text>
        </HStack>
      ) : displaySummary ? (
        <>
          <Text
            fontSize="sm"
            color="gray.700"
            lineHeight="1.6"
            noOfLines={isExpanded ? undefined : 5}
          >
            {displaySummary.summary}
          </Text>
          <Text
            as="button"
            fontSize="xs"
            fontWeight="600"
            color="brand.500"
            mt={1}
            _hover={{ color: 'brand.700' }}
            onClick={() => setIsExpanded(prev => !prev)}
            cursor="pointer"
          >
            {isExpanded ? t('previewSidebar.showLess') : t('previewSidebar.showMore')}
          </Text>
        </>
      ) : (
        <Text fontSize="sm" color="gray.500">{t('previewSidebar.noSummary')}</Text>
      )}
    </Box>
  )
}

function KeyEntitiesSection({
  file,
  entities: initialEntities,
}: {
  file: FileItem
  entities: KeyEntity[]
}) {
  const { t } = useTranslation('files')
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true })
  const [entities, setEntities] = useState<KeyEntity[]>(initialEntities)
  const needsExtraction = initialEntities.length === 0 && !!file.extracted_text
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>(
    needsExtraction ? 'loading' : 'success'
  )
  const extractionStartedRef = useRef(false)

  const fetchEntities = useCallback(async () => {
    setStatus('loading')
    try {
      const result = await apiService.extractKeyEntities(file.id)
      setEntities(result.key_entities || [])
      setStatus('success')
    } catch {
      setStatus('error')
    }
  }, [file.id])

  useEffect(() => {
    if (needsExtraction && !extractionStartedRef.current) {
      extractionStartedRef.current = true
      fetchEntities()
    }
  }, [needsExtraction, fetchEntities])

  if (!file.extracted_text) return null
  if (status === 'success' && entities.length === 0) return null

  return (
    <Box>
      <HStack
        justify="space-between"
        cursor="pointer"
        onClick={onToggle}
        py={1}
        _hover={{ opacity: 0.8 }}
      >
        <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wider">
          {t('previewSidebar.keyEntities')}
        </Text>
        <Icon as={isOpen ? FiChevronUp : FiChevronDown} color="gray.400" boxSize={4} />
      </HStack>
      <Collapse in={isOpen} animateOpacity>
        <VStack spacing={2} mt={2} align="stretch">
          {status === 'loading' ? (
            <HStack spacing={2} py={2}>
              <Spinner size="sm" color="brand.500" />
              <Text fontSize="sm" color="gray.500">{t('previewSidebar.detectingEntities')}</Text>
            </HStack>
          ) : status === 'error' ? (
            <HStack spacing={2} py={2}>
              <Icon as={FiAlertCircle} color="gray.400" boxSize={4} />
              <Text fontSize="sm" color="gray.500">{t('previewSidebar.entitiesError')}</Text>
              <Button size="xs" variant="link" colorScheme="brand" onClick={fetchEntities}>
                {t('previewSidebar.retryEntities')}
              </Button>
            </HStack>
          ) : (
            entities.map((entity, idx) => {
              const IconComp = entityIcons[entity.type] || FiCpu
              const color = entityColors[entity.type] || 'gray'
              return (
                <HStack
                  key={idx}
                  spacing={3}
                  py={2}
                  cursor="default"
                >
                  <Flex
                    w={8}
                    h={8}
                    bg="white"
                    borderRadius="full"
                    align="center"
                    justify="center"
                    flexShrink={0}
                    shadow="sm"
                  >
                    <Icon as={IconComp} color={`${color}.500`} boxSize={4} />
                  </Flex>
                  <VStack align="start" spacing={0} minW={0}>
                    <Text fontSize="sm" fontWeight="600" color="gray.800" noOfLines={1}>
                      {entity.name}
                    </Text>
                    <Text fontSize="xs" color="gray.500" noOfLines={1}>
                      {entity.role}
                    </Text>
                  </VStack>
                </HStack>
              )
            })
          )}
        </VStack>
      </Collapse>
    </Box>
  )
}

function TagsSection({ tags }: { tags: string[] }) {
  const { t } = useTranslation('files')
  if (tags.length === 0) return null

  return (
    <Box>
      <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={2}>
        {t('previewSidebar.documentIntelligence')}
      </Text>
      <Wrap spacing={2}>
        {tags.map((tag) => (
          <WrapItem key={tag}>
            <Tag
              size="sm"
              variant="subtle"
              colorScheme="blue"
              borderRadius="full"
              px={3}
              py={1}
            >
              <TagLabel fontSize="xs">#{tag}</TagLabel>
            </Tag>
          </WrapItem>
        ))}
      </Wrap>
    </Box>
  )
}

function ChatSection({ file, summaries, userLanguage, onSaveAsNote }: { file: FileItem; summaries: FileSummary[]; userLanguage: string; onSaveAsNote: (content: string) => Promise<void> }) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const toast = useToast()
  const { t } = useAppTranslation(['common'])
  const { send: sendToChat, stop: stopAnswer, isStreaming } = useChatStream()
  // Follow new text only while the reader is at the bottom; scrolling up pauses it
  const isNearBottomRef = useRef(true)

  const sessionStorageKey = `chat_session_file_${file.id}`

  const [sessionId, setSessionId] = useState<string>(() => {
    const stored = localStorage.getItem(`chat_session_file_${file.id}`)
    if (stored) return stored
    return `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  })

  const fileName = file.display_name || file.original_name || file.name

  const suggestedQuestions = useMemo(() => {
    const withQuestions = (s: FileSummary) =>
      s.status === 'completed' && (s.suggested_questions?.length ?? 0) > 0
    const docSummary =
      summaries?.find(s => s.language === userLanguage && withQuestions(s)) ??
      summaries?.find(s => s.language === 'en' && withQuestions(s)) ??
      summaries?.find(withQuestions)
    if (docSummary?.suggested_questions?.length) return docSummary.suggested_questions
    return t('chat.suggestedQuestions.file', { returnObjects: true }) as string[]
  }, [summaries, userLanguage, t])

  // While an answer streams, jump instead of animating so it doesn't stutter per token
  useEffect(() => {
    if (messages.length > 0 && isNearBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: isStreaming ? 'auto' : 'smooth', block: 'end' })
    }
  }, [messages, isStreaming])

  const handleMessagesScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
  }, [])

  // Load persisted history on mount
  useEffect(() => {
    const stored = localStorage.getItem(sessionStorageKey)
    if (!stored) return

    setIsLoadingHistory(true)
    apiService
      .getChatHistory(stored)
      .then(({ messages: rows }) => {
        if (rows.length === 0) return
        const loaded: ChatMessage[] = rows.flatMap((row) => [
          { id: `${row.created_at}_q`, type: 'user' as const, content: row.message, timestamp: new Date(row.created_at) },
          { id: `${row.created_at}_a`, type: 'assistant' as const, content: row.response, timestamp: new Date(row.created_at) },
        ])
        setMessages(loaded)
      })
      .catch(() => {})
      .finally(() => setIsLoadingHistory(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const clearChat = useCallback(async () => {
    await apiService.clearChatHistory(sessionId).catch(() => {})
    localStorage.removeItem(sessionStorageKey)
    const newSessionId = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    setSessionId(newSessionId)
    setMessages([])
    toast({ title: t('chat.chatCleared', { ns: 'common' }), status: 'info', duration: 2000, isClosable: true })
  }, [sessionId, sessionStorageKey, toast, t])

  const handleSend = useCallback(async (messageOverride?: string) => {
    const content = (messageOverride ?? inputValue).trim()
    if (!content) return

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content,
      timestamp: new Date(),
    }

    setMessages(prev => [...prev, userMessage])
    setInputValue('')
    setIsLoading(true)
    setIsTyping(true)

    // Persist session ID so history survives page reloads
    localStorage.setItem(sessionStorageKey, sessionId)
    isNearBottomRef.current = true

    // One id for the whole answer so it grows in place instead of re-animating
    const assistantId = `${Date.now()}_a`
    const updateAnswer = (patch: Partial<ChatMessage>) =>
      setMessages(prev =>
        prev.some(m => m.id === assistantId)
          ? prev.map(m => (m.id === assistantId ? { ...m, ...patch } : m))
          : [...prev, { id: assistantId, type: 'assistant', content: '', timestamp: new Date(), ...patch }]
      )

    const result = await sendToChat(
      {
        question: content,
        session_id: sessionId,
        file_id: file.id,
        conversation_history: toConversationHistory(messages),
      },
      {
        onText: text => {
          if (text === '') return
          setIsTyping(false)
          updateAnswer({ content: text })
        },
        onSuggestions: questions =>
          updateAnswer({ suggestedQuestions: questions.length ? questions : undefined }),
        onDone: answer => updateAnswer({ content: answer }),
      }
    )

    if (result.status === 'stopped' && result.text) {
      updateAnswer({ status: 'stopped' })
    } else if (result.status === 'error') {
      if (result.text && !isUsageLimitError(result.error)) {
        updateAnswer({ status: 'interrupted', excludeFromHistory: true })
      }
      setMessages(prev => [
        ...prev,
        {
          id: `${Date.now()}_n`,
          type: 'notice',
          content: isUsageLimitError(result.error)
            ? t('chatbot.limitExceeded', { ns: 'common' })
            : t('chat.errorGeneric', { ns: 'common' }),
          timestamp: new Date(),
          excludeFromHistory: true,
        },
      ])
    }

    setIsLoading(false)
    setIsTyping(false)
    setTimeout(() => inputRef.current?.focus(), 100)
  }, [inputValue, messages, sessionId, sessionStorageKey, file.id, sendToChat, t])

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend]
  )

  const copyMessage = useCallback(
    (content: string) => {
      navigator.clipboard.writeText(content).then(() => {
        toast({ title: t('chat.copied'), status: 'success', duration: 1500, isClosable: true })
      })
    },
    [toast]
  )

  const [savingNoteId, setSavingNoteId] = useState<string | null>(null)

  const saveMessageAsNote = useCallback(
    async (message: ChatMessage) => {
      setSavingNoteId(message.id)
      try {
        await onSaveAsNote(message.content)
        toast({ title: t('chat.savedAsNote'), status: 'success', duration: 2000, isClosable: true })
      } catch {
        toast({ title: t('chat.saveAsNoteError'), status: 'error', duration: 3000, isClosable: true })
      } finally {
        setSavingNoteId(null)
      }
    },
    [onSaveAsNote, toast, t]
  )

  const hasWelcome = messages.length === 0

  return (
    <Box display="flex" flexDirection="column" flex={1} minH={0} position="relative">
      {/* Chat messages area */}
      <Box
        flex={1}
        overflowY="auto"
        px={3}
        py={3}
        onScroll={handleMessagesScroll}
        css={{
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: '#CBD5E0', borderRadius: '2px' },
        }}
      >
        <VStack spacing={3} align="stretch">
          {isLoadingHistory && (
            <Flex justify="center" align="center" py={4}>
              <HStack spacing={1}>
                <Circle size="5px" bg="gray.400" animation="bounce 1.4s infinite" />
                <Circle size="5px" bg="gray.400" animation="bounce 1.4s infinite 0.2s" />
                <Circle size="5px" bg="gray.400" animation="bounce 1.4s infinite 0.4s" />
              </HStack>
            </Flex>
          )}

          {hasWelcome && !isLoadingHistory && (
            <MotionBox
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Flex gap={2} align="start">
                <Avatar size="xs" bg="brand.500" color="white" icon={<FaRobot />} />
                <Box
                  bg="gray.50"
                  p={3}
                  borderRadius="xl"
                  borderTopLeftRadius="sm"
                  flex={1}
                >
                  <Text fontSize="sm" color="gray.700">
                    {t('chat.analyzedFile', { fileName })}
                  </Text>
                </Box>
              </Flex>
            </MotionBox>
          )}

          {messages.map((message) => (
            <MotionBox
              key={message.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              {message.type === 'notice' ? (
                <Text fontSize="xs" color="gray.400" textAlign="center" py={1}>
                  {message.content}
                </Text>
              ) : message.type === 'user' ? (
                <Flex justify="flex-end" gap={2}>
                  <Box
                    bg="brand.500"
                    color="white"
                    p={2.5}
                    borderRadius="xl"
                    borderTopRightRadius="sm"
                    maxW="85%"
                  >
                    <Text fontSize="sm">{message.content}</Text>
                  </Box>
                </Flex>
              ) : (
                <Flex gap={2} align="start">
                  <Avatar size="xs" bg="brand.500" color="white" icon={<FaRobot />} flexShrink={0} />
                  <VStack align="start" spacing={1} flex={1} minW={0}>
                    <Box
                      bg="gray.50"
                      p={2.5}
                      borderRadius="xl"
                      borderTopLeftRadius="sm"
                      maxW="95%"
                    >
                      <BotMarkdown>{message.content}</BotMarkdown>
                    </Box>
                    <HStack spacing={0}>
                      <IconButton
                        icon={<Icon as={FaCopy} />}
                        variant="ghost"
                        size="xs"
                        color="gray.400"
                        _hover={{ color: 'brand.500' }}
                        aria-label={t('chat.copyMessage')}
                        onClick={() => copyMessage(message.content)}
                      />
                      <IconButton
                        icon={<Icon as={FiBookmark} />}
                        variant="ghost"
                        size="xs"
                        color="gray.400"
                        _hover={{ color: 'brand.500' }}
                        aria-label={t('chat.saveAsNote')}
                        isLoading={savingNoteId === message.id}
                        onClick={() => saveMessageAsNote(message)}
                      />
                      {message.status && (
                        <Text fontSize="xs" color="gray.400" fontStyle="italic" ml={1}>
                          {t(message.status === 'stopped' ? 'chat.stopped' : 'chat.interrupted', { ns: 'common' })}
                        </Text>
                      )}
                    </HStack>
                    {message.suggestedQuestions && message.suggestedQuestions.length > 0 && (
                      <VStack spacing={2} align="stretch" w="full">
                        <Text fontSize="xs" color="gray.600" fontWeight="medium">
                          {t('chat.suggestedFollowUps', { ns: 'common' })}
                        </Text>
                        {message.suggestedQuestions.map((suggestion, index) => (
                          <Button
                            key={index}
                            variant="outline"
                            size="xs"
                            borderRadius="full"
                            borderColor="brand.200"
                            color="brand.600"
                            fontWeight="normal"
                            fontSize="xs"
                            whiteSpace="normal"
                            height="auto"
                            py={1.5}
                            textAlign="left"
                            justifyContent="flex-start"
                            _hover={{ bg: 'brand.50', borderColor: 'brand.300' }}
                            onClick={() => handleSend(suggestion)}
                          >
                            {suggestion}
                          </Button>
                        ))}
                      </VStack>
                    )}
                  </VStack>
                </Flex>
              )}
            </MotionBox>
          ))}

          {isTyping && (
            <Flex gap={2} align="start">
              <Avatar size="xs" bg="brand.500" color="white" icon={<FaRobot />} />
              <Box
                bg="gray.50"
                p={2.5}
                borderRadius="xl"
                borderTopLeftRadius="sm"
              >
                <HStack spacing={1}>
                  <Circle size="5px" bg="gray.400" animation="bounce 1.4s infinite" />
                  <Circle size="5px" bg="gray.400" animation="bounce 1.4s infinite 0.2s" />
                  <Circle size="5px" bg="gray.400" animation="bounce 1.4s infinite 0.4s" />
                </HStack>
              </Box>
            </Flex>
          )}

          <div ref={messagesEndRef} />
        </VStack>
      </Box>

      {/* Input area */}
      <Box px={3} pb={3} pt={2} flexShrink={0}>
        {/* Suggested questions */}
        {hasWelcome && suggestedQuestions.length > 0 && (
          <Wrap spacing={1.5} mb={2}>
            {suggestedQuestions.slice(0, 4).map((q, i) => (
              <WrapItem key={i}>
                <Button
                  size="xs"
                  variant="outline"
                  borderRadius="full"
                  borderColor="brand.200"
                  color="brand.600"
                  fontWeight="normal"
                  fontSize="xs"
                  _hover={{ bg: 'brand.50', borderColor: 'brand.300' }}
                  onClick={() => handleSend(q)}
                >
                  {q}
                </Button>
              </WrapItem>
            ))}
          </Wrap>
        )}

        <HStack spacing={2}>
          <InputGroup size="sm">
            <Input
              ref={inputRef}
              placeholder={t('chat.askQuestion')}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="full"
              fontSize="sm"
              _focus={{
                borderColor: 'brand.500',
                boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
              }}
            />
          </InputGroup>
          {isStreaming ? (
            <IconButton
              aria-label={t('chat.stop', { ns: 'common' })}
              title={t('chat.stop', { ns: 'common' })}
              icon={<FiSquare size="12px" />}
              size="sm"
              colorScheme="red"
              variant="outline"
              borderRadius="full"
              onClick={stopAnswer}
            />
          ) : (
            <IconButton
              aria-label={t('chat.sendMessage')}
              icon={<FiSend size="14px" />}
              size="sm"
              colorScheme="brand"
              borderRadius="full"
              // Wrapped: passing handleSend directly would hand it the click event as the message
              onClick={() => handleSend()}
              isDisabled={!inputValue.trim() || isLoading}
              isLoading={isLoading}
            />
          )}
        </HStack>
        {messages.length > 0 && (
          <Button
            size="xs"
            variant="ghost"
            color="gray.400"
            _hover={{ color: 'red.400', bg: 'red.50' }}
            onClick={clearChat}
            alignSelf="flex-end"
          >
            {t('chat.clearChat', { ns: 'common' })}
          </Button>
        )}
      </Box>
    </Box>
  )
}

export default function PreviewSidebar({
  file,
  tags,
  summaries,
  fileLinksOutgoing = [],
  fileLinksIncoming = [],
  onFileLinksChanged,
}: PreviewSidebarProps) {
  const { currentLanguage } = useLanguage()
  const { t } = useAppTranslation(['common', 'files'])
  const [openSection, setOpenSection] = useState<'summary' | 'notes' | 'chat'>('summary')
  const notesState = useFileNotes(file.id)
  const { addNote } = notesState

  // Saving from the chat must not switch the open section away from the chat
  const handleSaveAsNote = useCallback(
    async (content: string) => {
      await addNote(content, 'private')
    },
    [addNote]
  )

  return (
    <Box
      w={{ base: 'full', lg: '380px' }}
      minW={{ lg: '340px' }}
      maxW={{ lg: '420px' }}
      h="full"
      borderLeft="1px solid"
      borderColor="gray.200"
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      {/* Summary accordion */}
      <Box
        bg="white"
        display="flex"
        flexDirection="column"
        flex={openSection === 'summary' ? 1 : 'none'}
        minH={0}
        overflow="hidden"
      >
        <HStack
          px={4}
          py={3}
          justify="space-between"
          cursor="pointer"
          onClick={() => setOpenSection('summary')}
          _hover={{ bg: 'gray.50' }}
          userSelect="none"
          flexShrink={0}
        >
          <HStack spacing={2}>
            <Icon as={HiSparkles} color="brand.500" boxSize={4} />
            <Text fontSize="sm" fontWeight="600" color="gray.700">
              {t('previewSidebar.summaryTitle', { ns: 'files' })}
            </Text>
          </HStack>
          <Icon as={openSection === 'summary' ? FiChevronUp : FiChevronDown} color="gray.400" boxSize={4} />
        </HStack>
        {openSection === 'summary' && (
          <Box
            flex={1}
            minH={0}
            overflowY="auto"
            display="flex"
            flexDirection="column"
            css={{
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: '#CBD5E0', borderRadius: '2px' },
            }}
          >
            {/* AI Summary — gradient background */}
            <Box bg="#e3f2fd" px={4} py={4} flexShrink={0}>
              <AISummarySection summaries={summaries} userLanguage={currentLanguage} />
            </Box>

            {/* Entities + Tags — neutral gray background */}
            <Box bg="gray.50" px={4} py={3} flex={1}>
              <VStack spacing={4} align="stretch">
                <LinkedDocumentsSection
                  file={file}
                  outgoing={fileLinksOutgoing}
                  incoming={fileLinksIncoming}
                  onRefresh={() => onFileLinksChanged?.()}
                />
                <KeyEntitiesSection key={file.id} file={file} entities={file.key_entities || []} />
                <TagsSection tags={tags} />
              </VStack>
            </Box>
          </Box>
        )}
      </Box>

      {/* Notes accordion */}
      <Box
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        display="flex"
        flexDirection="column"
        flex={openSection === 'notes' ? 1 : 'none'}
        minH={0}
        overflow="hidden"
      >
        <NotesSection
          isOpen={openSection === 'notes'}
          onToggle={() => setOpenSection('notes')}
          notesState={notesState}
        />
      </Box>

      {/* Chat accordion */}
      <Box
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        display="flex"
        flexDirection="column"
        flex={openSection === 'chat' ? 1 : 'none'}
        minH={0}
        overflow="hidden"
      >
        <HStack
          px={4}
          py={3}
          justify="space-between"
          cursor="pointer"
          onClick={() => setOpenSection('chat')}
          _hover={{ bg: 'gray.50' }}
          userSelect="none"
          flexShrink={0}
        >
          <HStack spacing={2}>
            <Icon as={FaRobot} color="brand.500" boxSize={4} />
            <Text fontSize="sm" fontWeight="600" color="gray.700">
              {t('chat.aiAssistant')}
            </Text>
          </HStack>
          <Icon as={openSection === 'chat' ? FiChevronUp : FiChevronDown} color="gray.400" boxSize={4} />
        </HStack>
        {openSection === 'chat' && (
          <ChatSection
            file={file}
            summaries={summaries}
            userLanguage={currentLanguage}
            onSaveAsNote={handleSaveAsNote}
          />
        )}
      </Box>
    </Box>
  )
}
